/**
 * These functions are designed to return FAQ answers in Markdown format.
 * To render an answer, pass the response to the "content" prop of the Markdown component.
 */
import {
  registrationNotRequired,
  registrationPrintMailDisabled,
  registrationPrintMailRequiresNotary,
  registrationEligibilityDaysFrom18,
} from '~components/Register/specialCases';
import {
  formatLocalElectionOfficeLink,
  nvraUrl,
  getRegistrationForm,
  formatMailDeadline,
  getNoRegistrationStatement,
} from '~utils/faqAnswerUtils';
import { nextElection } from '~utils/displayOptions';
import { capitalize } from '~utils/helpers';
import { formatDateFriendly } from '~utils/formatDate';
import formatAge from '~utils/formatAge';

const stubOnlineInstructions = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  const paragraphs = [];

  if (stateInfo.registration_methods.multiselect_value.includes('online')) {
    paragraphs.push(stateInfo.id_laws_online_registration.text);
    paragraphs.push('Here are the instructions for registering online:');

    const steps = [];
    steps.push(
      `1. Visit the [${stateName} online voter registration site](${stateInfo.gov_tool_registration.text}).`
    );
    steps.push('2. Fill out all the required fields.');
    steps.push(
      `3. You should receive your voter registration card in the mail within 2-3 weeks. 
      If not, you might want to follow up with your ${formatLocalElectionOfficeLink(stateCode)}.`
    );
    paragraphs.push(steps.join('\n'));

    paragraphs.push(
      `The deadline to register to vote online is ${stateInfo.registration_deadline_online.text}.`
    );
  } else {
    paragraphs.push(`${stateName} doesn't offer online voter registration.`);
  }
  return paragraphs.join('\n\n');
};

const stubMailInstructions = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  const paragraphs = [];

  if (stateInfo.registration_directions_mail.text) {
    paragraphs.push(stateInfo.registration_directions_mail.text);
    paragraphs.push(`If registering by mail, your voter registration must be 
    ${formatMailDeadline(stateInfo.registration_deadline_mail.text)}.`);
  } else {
    const steps = [];
    steps.push(
      `1. Download and fill out the [National Voter Registration form](${nvraUrl}).`
    );
    steps.push(`2. Box 6 - ID Number: ${stateInfo.nvra_directions_box6.text}`);
    steps.push(
      `3. Box 7 - Choice of Party: ${stateInfo.nvra_directions_box7.text}`
    );
    steps.push(
      `4. Box 8 - Race or Ethnic Group: ${stateInfo.nvra_directions_box8.text}`
    );
    steps.push(`5. Print, date, and sign the form. If you do not have a printer, you can print the form at any local library. 
      If you do not have a stamp, you can buy one from any grocery store: just ask the cashier to sell you one.`);
    steps.push(
      `6. Mail your voter registration form to your ${formatLocalElectionOfficeLink(
        stateCode
      )}.`
    );
    paragraphs.push(steps.join('\n'));

    paragraphs.push(
      `Your voter registration must be ${formatMailDeadline(stateInfo.registration_deadline_mail.text)}.`
    );
  }

  return paragraphs.join('\n\n');
};

const stubInPersonInstructions = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  const paragraphs = [];
  paragraphs.push(
    `Contact your ${formatLocalElectionOfficeLink(stateCode)} to find out where to register in person.`
  );

  if (stateInfo.id_laws_sdr.text && stateInfo.id_laws_sdr.text !== 'N/A') {
    paragraphs.push(stateInfo.id_laws_sdr.text);
  } else {
    paragraphs.push(
      `Bring your ${stateName} driver's license or state ID card with you. If you don't have one, 
      bring ONE photo ID that shows your name, and ONE document that shows your name and ${stateName} address. 
      Acceptable documents include a utility bill, bank statement, paycheck, residential lease, 
      or any government document that shows your name and address.`
    );
  }

  paragraphs.push(
    `The deadline to register in person is ${stateInfo.registration_deadline_in_person.text}.`
  );

  return paragraphs.join('\n\n');
};

export const whatAreTheVoterRegistrationDeadlines = (
  stateInfo,
  stateName,
  stateCode
) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  const hasElectionDayRegistration =
    stateInfo.has_sdr_election_day.bool_value === true;
  const hasEarlyVotingRegistration =
    stateInfo.has_sdr_early_voting.bool_value === true;

  const paragraphs = [];
  paragraphs.push(
    `Voter registration deadlines in ${stateName} are as follows:`
  );

  const items = [];

  if (stateInfo.registration_methods.multiselect_value.includes('online')) {
    items.push(
      `* [Online](${stateInfo.gov_tool_registration.text}): ${stateInfo.registration_deadline_online.text}`
    );
  }
  if (stateInfo.registration_methods.multiselect_value.includes('mail')) {
    items.push(
      `* [By mail](${getRegistrationForm(stateInfo, stateCode)}): ${capitalize(formatMailDeadline(stateInfo.registration_deadline_mail.text))}`
    );
  }
  items.push(
    `* [In person](${stateInfo.source_registration_info.text}): ${stateInfo.registration_deadline_in_person.text} ${hasElectionDayRegistration ? '(via same-day registration)' : ''}${!hasElectionDayRegistration && hasEarlyVotingRegistration ? '(via registration during the early vote period)' : ''}`
  );

  paragraphs.push(items.join('\n'));

  return paragraphs.join('\n\n');
};

export const amIRegistered = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  // If this is a PDF, it's a directory of LEOs rather than an actual tool
  const linkToStateTool =
    stateInfo.gov_tool_verify.text &&
    !stateInfo.gov_tool_verify.text.endsWith('.pdf');

  return `You can check your ${stateName} voter registration 
  ${
    linkToStateTool
      ? `using the [${stateName} voter registration lookup site](${stateInfo.gov_tool_verify.text}).`
      : `by contacting your ${formatLocalElectionOfficeLink(stateCode)}.`
  }`;
};

export const whoCanRegister = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }
  return stateInfo.registration_laws.text;
};

export const canIRegisterOnline = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  if (stateInfo.registration_methods.multiselect_value.includes('online')) {
    return `Yes. ${stubOnlineInstructions(stateInfo, stateName, stateCode)}`;
  }

  const includeMailAlternative =
    stateInfo.registration_methods.multiselect_value.includes('mail') &&
    !registrationPrintMailDisabled.includes(stateCode);

  const paragraphs = [];
  paragraphs.push(`No, ${stateName} does not offer online voter registration.`);
  paragraphs.push(
    `Instead, you can ${includeMailAlternative ? `[print and mail a form](${getRegistrationForm(stateInfo, stateCode)}), or` : ''} contact your ${formatLocalElectionOfficeLink(stateCode)} to register in person.`
  );
  return paragraphs.join('\n\n');
};

export const canIRegisterViaMail = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  const prependWithYes =
    !registrationPrintMailDisabled.includes(stateCode) &&
    !registrationPrintMailRequiresNotary.includes(stateCode);
  if (stateInfo.registration_directions_mail.text) {
    return `${prependWithYes ? 'Yes. ' : ''}${stubMailInstructions(stateInfo, stateName, stateCode)}`;
  }

  const paragraphs = [];
  paragraphs.push(
    `Yes. Follow the instructions below to register via mail in ${stateName}:`
  );
  paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));
  return paragraphs.join('\n\n');
};

export const canIRegisterInPerson = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  return `Yes. ${stubInPersonInstructions(stateInfo, stateName, stateCode)}`;
};

export const canIRegisterOnElectionDay = (
  stateInfo,
  stateName,
  stateCode,
  includeDeadlineAnchorLink = true
) => {
  if (registrationNotRequired.includes(stateCode)) {
    return getNoRegistrationStatement(stateName);
  }

  if (
    stateInfo.has_sdr_early_voting.bool_value === true ||
    stateInfo.has_sdr_election_day.bool_value === true
  ) {
    const paragraphs = [];

    if (
      stateInfo.has_sdr_early_voting.bool_value === true &&
      stateInfo.has_sdr_election_day.bool_value === true
    ) {
      paragraphs.push(
        `Yes! ${stateName} has same-day registration. You can register and vote during the early voting period and on Election Day.`
      );
    } else if (stateInfo.has_sdr_election_day.bool_value === true) {
      paragraphs.push(
        `Yes! ${stateName} has same-day registration. You can register and vote on Election Day.`
      );
    } else {
      paragraphs.push(
        `No. However, in ${stateName}, you can register and vote during the early voting period, 
        which starts ${stateInfo.early_voting_starts.text} and ends ${stateInfo.early_voting_ends.text}.`
      );
    }

    const bullets = [];
    bullets.push(`- **Where to go:** ${stateInfo.sdr_locations.text}`);
    bullets.push(`- **What to bring:** ${stateInfo.id_laws_sdr.text}`);

    const footnotes = [];
    if (stateInfo.has_sdr_early_voting.footnotes) {
      footnotes.push(stateInfo.has_sdr_early_voting.footnotes);
    }
    // In some cases, the same footnote is on both the early voting and Election Day fields - avoid duplicating
    if (
      stateInfo.has_sdr_election_day.footnotes &&
      !footnotes.includes(stateInfo.has_sdr_election_day.footnotes)
    ) {
      footnotes.push(stateInfo.has_sdr_election_day.footnotes);
    }
    if (footnotes.length > 0) {
      bullets.push(`- **Notes:** ${footnotes.join(' ')}`);
    }

    paragraphs.push(bullets.join('\n'));

    return paragraphs.join('\n\n');
  }

  return `Unfortunately, no. In ${
    stateName
  }, you must register in advance of Election Day. 
  ${
    includeDeadlineAnchorLink
      ? '[See voter registration deadlines here](#deadlines-register).'
      : ''
  }`;
};

export const doINeedToUpdateRegistrationAfterMove = (
  stateInfo,
  stateName,
  stateCode,
  includeDeadlineAnchorLink = true
) => {
  const paragraphs = [];

  paragraphs.push(
    'Yes. Whenever you move, you need to register to vote with your new address.'
  );

  paragraphs.push(
    `${
      includeDeadlineAnchorLink &&
      'General voter registration deadlines are [listed above](#deadlines-register).'
    } 
    If you moved to a new state, you must register with the new state before the registration deadline 
    in order to be able to vote in that state for the upcoming election. 
    If you moved within a state and the registration deadline has passed, 
    you may still be able to vote, depending on the type of election and your state’s rules. 
    Check with your ${formatLocalElectionOfficeLink(
      stateCode
    )} for more information.`
  );

  if (stateInfo.registration_methods.multiselect_value.includes('online')) {
    paragraphs.push('#### **Online**');

    paragraphs.push(
      `${stateName} offers [online voter registration](${stateInfo.gov_tool_registration.text}), 
      but if you just moved, you might not be eligible to use it.`
    );

    paragraphs.push(stateInfo.id_laws_online_registration.text);
    paragraphs.push(
      `The deadline to register online is ${stateInfo.registration_deadline_online.text}.`
    );
  }

  if (stateInfo.registration_methods.multiselect_value.includes('mail')) {
    paragraphs.push('#### **By mail**');
    paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));
  }

  if (stateInfo.registration_methods.multiselect_value.includes('in_person')) {
    paragraphs.push('#### **In person**');
    paragraphs.push(stubInPersonInstructions(stateInfo, stateName, stateCode));
  }

  return paragraphs.join('\n\n');
};

export const whenCanIPreregister = (stateInfo, stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return `${stateName} does not have voter registration. Just bring your ID to the polls and vote once you turn 18.`;
  }

  const electionDate = new Date(nextElection);

  const paragraphs = [];
  if (stateInfo.has_preregistration.bool_value) {
    if (stateCode in registrationEligibilityDaysFrom18) {
      paragraphs.push(
        `You can pre-register in ${stateName} if you will turn 18 sometime in the next ${registrationEligibilityDaysFrom18[stateCode]} days.`
      );
    } else {
      paragraphs.push(
        `You can pre-register in ${stateName} if you are at least ${formatAge(stateInfo.minimum_age_register.text)} years old.`
      );
    }
  } else {
    paragraphs.push(
      `You can register now in ${stateName} if you will be 18 by the next major election on ${formatDateFriendly(electionDate)}
      (as long as the [registration deadline](#deadlines-register) for that election has not passed).`
    );
  }

  const bullets = [];
  bullets.push(
    '* Your registration will activate automatically on your 18th birthday.'
  );
  bullets.push(
    '* Our [FutureVoter tool](/futurevoter/) can walk you through the process.'
  );
  paragraphs.push(bullets.join('\n'));

  return paragraphs.join('\n\n');
};

export const canIRegisterIfIHaveAFelonyConviction = (stateInfo) => {
  const paragraphs = [];
  paragraphs.push(stateInfo.felony_laws_restoration_process.text);
  paragraphs.push(
    'Please visit [Restore Your Vote](https://campaignlegal.org/restoreyourvote) ' +
      'for additional information. They have attorneys on staff who can guide you.'
  );
  return paragraphs.join('\n\n');
};

export const doINeedToRegister = (stateName, stateCode) => {
  if (registrationNotRequired.includes(stateCode)) {
    return `No! ${stateName} doesn't have voter registration. Just bring your ID to the polls and vote.`;
  }
  return `Yes, you must register before you can vote in ${stateName}.`;
};
